import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, type ReviewEntry, type ReviewPage, type ReviewSide } from '../lib/api';
import { MergeInterface } from './MergeInterface';

/**
 * Review queue — Appendix C.2 Phase 2 deliverable 11.
 *
 * The queue exists because §3.6 disables automatic fuzzy merge permanently and
 * C.2 requires the three duplicate candidates to "enter the review queue rather
 * than merging". Nothing on this screen happens implicitly: every action carries
 * a note, is attributed to the signed-in person, and is reversible.
 *
 * Each entry states the signal, the blueprint's recommendation and the binding
 * spec text side by side, because for 156/175 those two disagree and the person
 * deciding needs to see both rather than a summary of one.
 */

const KIND_LABEL: Record<string, string> = {
  duplicate_candidate: 'Duplicate candidate',
  household_link: 'Household link',
  unreachable_tier_c: 'Unreachable (tier C)',
  address_only_converted: 'Address-only record',
};

const STATUS_TONE: Record<string, string> = {
  open: 'border-royal text-royal',
  held: 'border-warning text-warning',
  merged: 'border-success text-success',
  rejected: 'border-text-secondary text-text-secondary',
  resolved: 'border-success text-success',
};

/**
 * `all` is sent to the server rather than left as an absent parameter, so the
 * request states the tab's intent; the route accepts it as "no status filter".
 */
const ALL_STATUSES = 'all';

const FILTERS = [
  { label: 'Open', status: 'open' },
  { label: 'Held', status: 'held' },
  { label: 'Merged', status: 'merged' },
  { label: 'Rejected', status: 'rejected' },
  { label: 'All', status: ALL_STATUSES },
] as const;

function SideCard({ side, label }: { side: ReviewSide; label: string }): JSX.Element {
  return (
    <div className="rounded-card border border-border bg-white p-3">
      <p className="micro-label text-text-secondary">{label}</p>
      <p className="mt-1 text-h3 font-semibold text-text-primary">{side.name ?? '—'}</p>
      {side.company ? <p className="text-body text-text-secondary">{side.company}</p> : null}
      <dl className="mt-2 grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-body">
        {[
          ['Legacy ID', side.legacy],
          ['Email', side.email],
          ['Phone', side.phone],
          ['Address', side.address],
          ['City / state', [side.city, side.state].filter(Boolean).join(', ') || null],
          ['Entity', side.pillars?.join(', ') || null],
          ['Tier', side.tier],
          ['Category', side.category?.replace(/_/g, ' ') ?? null],
        ].map(([term, value]) => (
          <div key={term as string} className="contents">
            <dt className="micro-label pt-1 text-text-secondary">{term}</dt>
            <dd className={term === 'Phone' || term === 'Legacy ID' ? 'numeric text-numeric' : ''}>
              {(value as string | null) ?? '—'}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function ReviewQueue(): JSX.Element {
  const [status, setStatus] = useState<string>('open');
  const [pageNumber, setPageNumber] = useState(1);
  const [page, setPage] = useState<ReviewPage | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [active, setActive] = useState<ReviewEntry | null>(null);
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPage(await api.reviewQueue({ page: pageNumber, status: status || undefined }));
      setNotice(null);
    } catch (failure) {
      setPage(null);
      setNotice(
        failure instanceof ApiFailure && failure.status === 403
          ? 'That entity scope is not available to your account.'
          : 'The review queue could not be loaded.',
      );
    }
  }, [pageNumber, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function resolve(entry: ReviewEntry, action: 'reject' | 'resolve' | 'hold' | 'reopen'): Promise<void> {
    const note = (noteDraft[entry.id] ?? '').trim();
    if (note.length < 3) {
      setNotice('A decision is recorded with a note naming the reason, or it is not recorded at all.');
      return;
    }
    setBusyId(entry.id);
    try {
      await api.resolveReview(entry.id, action, note);
      setNoteDraft((current) => ({ ...current, [entry.id]: '' }));
      await load();
    } catch (failure) {
      setNotice(failure instanceof ApiFailure ? `Refused: ${failure.body.error}` : 'That action failed.');
    } finally {
      setBusyId(null);
    }
  }

  const entries = page?.entries ?? [];

  return (
    <section aria-labelledby="review-heading">
      <h1 id="review-heading" className="mb-1 text-h1 font-semibold text-text-primary">
        Review queue
      </h1>
      <p className="mb-4 text-body text-text-secondary">
        Consolidation is never inferred at import. Every entry here waits for a named person, and every
        decision is reversible.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((filter) => {
          const isActive = status === filter.status;
          // The All tab counts every status, not the current filter's total —
          // reading `total` here made it render "All (0)" whenever the selected
          // tab was empty, which it was as soon as every entry was held or merged.
          const count =
            filter.status === ALL_STATUSES
              ? page?.totalAllStatuses
              : (page?.countsByStatus[filter.status] ?? 0);
          return (
            <button
              key={filter.label}
              type="button"
              aria-pressed={isActive}
              onClick={() => {
                setStatus(filter.status);
                setPageNumber(1);
              }}
              className={`rounded-card px-3 text-body font-medium ${
                isActive ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              {filter.label}
              {count === undefined ? '' : ` (${count})`}
            </button>
          );
        })}
      </div>

      {notice ? (
        <p role="status" className="mb-4 text-body text-warning">
          {notice}
        </p>
      ) : null}

      {entries.length === 0 ? (
        <div className="card rounded-card border border-border bg-white p-6">
          <p className="text-body text-text-secondary">Nothing is waiting in this view.</p>
        </div>
      ) : null}

      <ul className="grid gap-4">
        {entries.map((entry) => (
          <li key={entry.id} className="card rounded-card border border-border bg-white p-4">
            <header className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="micro-label text-text-secondary">
                  {KIND_LABEL[entry.kind] ?? entry.kind}
                  {entry.legacy_rows ? ` · legacy rows ${entry.legacy_rows}` : ''}
                  {entry.decision_ref ? ` · ${entry.decision_ref}` : ''}
                </p>
                <p className="numeric text-numeric text-text-secondary">{entry.decision_key}</p>
              </div>
              <span
                className={`micro-label rounded-card border px-2 py-1 ${
                  STATUS_TONE[entry.status] ?? 'border-border text-text-secondary'
                }`}
              >
                {entry.status}
              </span>
            </header>

            <dl className="mt-3 grid gap-2 text-body">
              <div>
                <dt className="micro-label text-text-secondary">Signal</dt>
                <dd>{entry.signal}</dd>
              </div>
              {entry.recommendation ? (
                <div>
                  <dt className="micro-label text-text-secondary">Blueprint recommendation</dt>
                  <dd>{entry.recommendation}</dd>
                </div>
              ) : null}
              {entry.binding_spec ? (
                <div>
                  <dt className="micro-label text-text-secondary">Binding spec (governs)</dt>
                  <dd>{entry.binding_spec}</dd>
                </div>
              ) : null}
              {entry.blocked_reason ? (
                <div>
                  <dt className="micro-label text-text-secondary">Why this is held</dt>
                  <dd className="text-warning">{entry.blocked_reason}</dd>
                </div>
              ) : null}
              {entry.resolution_note ? (
                <div>
                  <dt className="micro-label text-text-secondary">
                    Resolution{entry.resolved_by_name ? ` · ${entry.resolved_by_name}` : ''}
                  </dt>
                  <dd>{entry.resolution_note}</dd>
                </div>
              ) : null}
            </dl>

            {entry.a.id || entry.b.id ? (
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <SideCard side={entry.a} label="Record A" />
                <SideCard side={entry.b} label="Record B" />
              </div>
            ) : null}

            {entry.status === 'open' || entry.status === 'held' ? (
              <div className="mt-3 grid gap-2">
                <label htmlFor={`note-${entry.id}`} className="micro-label text-text-secondary">
                  Decision note (recorded against your name)
                </label>
                <textarea
                  id={`note-${entry.id}`}
                  rows={2}
                  value={noteDraft[entry.id] ?? ''}
                  onChange={(event) =>
                    setNoteDraft((current) => ({ ...current, [entry.id]: event.target.value }))
                  }
                  className="w-full rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
                  placeholder="e.g. CALM confirmed in writing on 6 Sept that these are one contact"
                />
                <div className="flex flex-wrap gap-2">
                  {entry.a.id && entry.b.id ? (
                    <button
                      type="button"
                      onClick={() => setActive(entry)}
                      className="btn-primary rounded-card px-4 text-body font-medium"
                    >
                      Open merge interface
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={busyId === entry.id}
                    onClick={() => void resolve(entry, 'reject')}
                    className="btn-destructive rounded-card px-4 text-body font-medium disabled:opacity-40"
                  >
                    Not a duplicate
                  </button>
                  <button
                    type="button"
                    disabled={busyId === entry.id}
                    onClick={() => void resolve(entry, entry.status === 'held' ? 'reopen' : 'hold')}
                    className="btn-secondary rounded-card px-4 text-body font-medium disabled:opacity-40"
                  >
                    {entry.status === 'held' ? 'Reopen' : 'Hold'}
                  </button>
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {page && (page.hasMore || page.page > 1) ? (
        <nav aria-label="Review queue pagination" className="mt-4 flex items-center justify-between gap-3">
          <p className="numeric text-numeric text-text-secondary">
            Page {page.page} · {page.pageSize} rows per page · {page.total} total
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page.page <= 1}
              onClick={() => setPageNumber(page.page - 1)}
              className="btn-secondary rounded-card px-4 text-body font-medium disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!page.hasMore}
              onClick={() => setPageNumber(page.page + 1)}
              className="btn-secondary rounded-card px-4 text-body font-medium disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </nav>
      ) : null}

      {active ? (
        <MergeInterface
          entry={active}
          onClose={() => setActive(null)}
          onMerged={() => {
            setActive(null);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}
